<template>
	<div>
		<v-data-table
				:headers="headers"
				:page.sync="page"
				:items="items"
				:sort-by.sync="sortBy"
				:sort-desc.sync="sortDesc"
				class="workOrdersTable"
				:items-per-page="getItemsPerPage"
				@page-count="pageCount = $event"
				hide-default-footer
				hide-default-header
				hide-default-body
				fixed-header
		>
			<template v-slot:header="{ props: { headers } }">
				<thead>
				<tr>
					<th
							v-for="(item, index) in headers"
							:class="[{'filter-active': filterClasses[item.value] || filterClassesActive(item.value)},
                                {'dense': item.dense}]"
							:key="index"
							class=""
							:id="'container-'+item.value"
					>
						<div class="filterIcons">
							<sort-btn
									v-if="item.sortable"
									:sortDesc="sortDesc"
									:sortBy="sortBy"
									:data-sort="item.value"
									@updateToggleOrder="toggleOrder"
							></sort-btn>
							<span class="headText wh_w"
							      :class="{'headText_sorted': getSortColor(sortBy, item.value), 'center': item.value === 'optional'}">
                                {{ item.text }}
                            </span>
							<filter-drop-down v-if="item.func" :item="item">
								<filter-table
										@change-sort="changeSort"
										@create-refresh="createRefresh"
										@filter-created="filterCreated"
										@updateFilterListAll="onFilteredAll(item)"
										:data-filter="filter[item.value]"
										:type-filter="item.model"
										:model-filter="item.value"
										:filter-update="filterUpdate"
										:is-disabled="filterPreloader"
										:isRefresh="isRefresh"
										:uniqId="uniqId"
										:filterStatus="workOrdersFilterDefault['filter_status']"
								></filter-table>
							</filter-drop-down>
						</div>
					</th>
				</tr>
				</thead>
			</template>
			<template v-slot:body="{ items }">
				<tbody v-if="isVisibleItems">
				<template v-for="(item, idx) in items">
					<table-tr
							:item="item"
							:key="item.id"
							:idx="idx"
							:checkClasses="checkClasses"
							:permissions="permissions"
							:filterClassesActive="filterClassesActive"
							@updateEdit="onEdit"
							@updateRemoveItem="onRemoveItem"
							@updateCommentsPopup="onCommentsPopup"
					></table-tr>
				</template>
				<tr class="tableBottom">
					<td>n: <span>{{ listItems.length }}</span></td>
					<td :key="i" v-for="i in 10"></td>
					<td>Σ: $<span>{{ getTotal }}</span></td>
				</tr>
				</tbody>
			</template>
		</v-data-table>
		<pagination
				v-if="items.length && isVisibleItems"
				:length="items.length"
				:pageCount="pageCount"
				:itemsPerPage="itemsPerPage"
				:page="page"
				:isRefresh="isRefresh"
				:isRefreshPage="isRefreshPage"
				:pages="pages"
				@updatePagination="onPagination"
				@updateCountPagination="onCountPagination"
		></pagination>
	</div>
</template>

<script>
import { mapActions } from "vuex";
// components
import TableTr from "@/components/Fleet/WorkOrders/WorkOrdersTable/TableTr/TableTr.vue";
import FilterTable
from "@/components/Maintenance/Customer/CustomerForm/SixthTab/FilterTable/FilterTable.vue";
import SortBtn from "@/shared/SortBtn/SortBtn.vue";
import FilterDropDown from "@/shared/FilterDropDown/FilterDropDown.vue";
// shared
import Pagination from "@/shared/Pagination/Pagination.vue";
// constants
import { OPTIONAL_KEYS } from "@/constants/constants";
import { workOrdersHeader, workOrdersListHeader } from "@/constants/defaultHeader";
// helpers
import getUniqId from "@/helper/getUniqId";
import debounce from 'lodash.debounce';
import { formatCurrency } from "@/helper";

export default {
	name: "WorkOrdersTable",
	components: {
		TableTr,
		FilterTable,
		Pagination,
		SortBtn,
		FilterDropDown
	},
	props: {
		listItems: Array,
		filter: Object,
		workOrdersFilterDefault: Object,
		permissions: Object,
		isRefresh: String,
		isFilterList: String,
		defValue: {
			value: [ Number, String ]
		},
		defFilter: String
	},
	computed: {
		headers() {
			return workOrdersHeader;
		},
		getItemsPerPage() {
			return this.itemsPerPage === 'All' ? 99999999 : this.itemsPerPage;
		},
		getTotal() {
			const total = this.listItems.reduce((acc, item) => acc += +item.total, 0);
			return formatCurrency(total);
		},
		items: {
			get() {
				return this.list;
			},
			set(arr) {
				this.list = arr;
			},
		},
		filterClasses: {
			get() {
				return this.checkClasses;
			},
			set(arr) {
				const obj = {};
				for (const i in arr) {
					obj[i] = arr[i].length;
				}
				this.checkClasses = obj;
			},
		},
	},
	watch: {
		listItems(val) {
			this.items = val;
		},
		async isFilterList(val, oldVal) {
			if (val !== oldVal) {
				await this.onWorkOrdersFilter(this.filters);
			}
		},
		async isRefresh(val, oldVal) {
			if (val !== oldVal) {
				await this.refreshFilters();
			}
		}
	},
	data() {
		return {
			pages: [ 10, 20, 50, 100, 200, 500 ],
			itemsPerPage: 10,
			page: 1,
			pageCount: 0,
			filterUpdate: false,
			sortBy: "id",
			sortDesc: true,
			menu: false,
			filtersMarker: {},
			filters: {},
			filtersList: {},
			list: this.listItems,
			checkClasses: {},
			filterPreloader: false,
			isRefreshPage: '',
			uniqId: { key: '', id: '' },
			isVisibleItems: false
		};
	},
	methods: {
		...mapActions([ 'onWorkOrdersFilter', 'onWorkOrdersPreloader' ]),
		onFilteredAll(item) {
			this.filterCreated(item.value);
			this.uniqId = {
				id: getUniqId(),
				key: item.value
			};
		},
		filterClassesActive(filterKey) {
			const { filter_check_in_empty, filter_check_out_empty } = this.filters || {};
			return (filterKey === "filter_check_in" && filter_check_in_empty)
					|| (filterKey === "filter_check_out" && filter_check_out_empty);
		},
		onEdit(id) {
			this.$emit('updateEdit', id);
		},
		onCommentsPopup(values) {
			this.$emit('updateCommentsPopup', values);
		},
		onRemoveItem(id) {
			this.$emit('updateRemoveItem', id);
		},
		onPagination(value) {
			this.page = value;
		},
		onCountPagination(value) {
			this.itemsPerPage = value;
		},
		async refreshFilters() {
			await this.onWorkOrdersPreloader(true);
			this.search = "";
			this.sortDesc = false;
			this.sortBy = "id";
			this.items = this.listItems;
			this.page = 1;

			if (Object.values(this.filterClasses)) {
				for (const i in this.filters) {
					const isOptional = OPTIONAL_KEYS.some(item => i.includes(item));
					this.filters[i] = isOptional ? -1 : [];
				}

				this.filters['filter_reviewed'] = 0;
				this.filterUpdate = true;
			}

			await this.onStartFilter();
		},
		createRefresh(model) {
			this.filtersMarker[model] = true;
			if (!Object.values(this.filtersMarker).includes(false)) {
				this.filterUpdate = false;
				for (const i in this.filtersMarker) {
					this.filtersMarker[i] = true;
				}
			}
			this.filterClasses = this.filters;
		},
		filterCreated(model) {
			this.filtersMarker[model] = false;
		},
		getSortColor(sortBy, value) {
			return sortBy === value || (sortBy && value.includes(sortBy));
		},
		toggleOrder(val) {
			const { value } = workOrdersListHeader.find(item => item.valueFilter === val);
			this.sortBy = value;
			this.sortDesc = !this.sortDesc;
		},
		initWorkOrdersFilter: debounce(async function(filters) {
			await this.onWorkOrdersFilter(filters);
			this.isRefreshPage = getUniqId();
			this.filterPreloader = false;
		}, 800),
		changeSort({ key, value, model }) {
			switch (model) {
				case 'date':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key]['from'];
					break;
				case 'number':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key]['type'];
					break;
				case 'note':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key].length;
					break;
				case 'options':
					this.filters[key] = Number(value);
					this.filterClasses[key] = !!this.filters[key].length;
					break;
				case 'checkbox':
					this.filters[key] = Number(value);
					break;
				default:
					this.filters[key] = value.map(item => item);
					this.filterClasses[key] = !!this.filters[key].length;
			}

			this.filterPreloader = true;
			this.initWorkOrdersFilter(this.filters);
		},
		async onStartFilter() {
			this.filterPreloader = true;
			if (!this.workOrdersFilterDefault) return false;
			this.filters[this.defFilter] = [ this.defValue ];
			await this.onWorkOrdersFilter(this.filters);
			this.filterPreloader = false;
			this.isVisibleItems = true;
			await this.onWorkOrdersPreloader(false);
		},
		async getFilterList() {
			for (let i = 0; i < this.headers.length; i++) {
				this.filtersMarker[this.headers[i].value] = true;
				this.$set(this.checkClasses, this.headers[i].value, false);
				const value = this.headers[i].value;

				if (value.includes('filter')) {
					this.filters[value] = [];
				}

				this.filtersList[value] = [];
			}

			await this.onStartFilter();
		}
	},
	created() {
		this.getFilterList();
	}
};
</script>

<style scoped lang="scss">
//@import "workOrdersTable";
</style>

<style lang="scss">
//.workOrdersTable {
//	.v-data-table__wrapper {
//		max-height: calc(100vh - (164px + 55px));
//	}
//}
</style>
